import Api from "../../api/Api";
import { toast } from "react-toastify";
import {
  ADD_GOOGLE_SUCCESS,
  DELETE_GOOGLE_SUCCESS,
  GET_GOOGLE_SUCCESS,
  UPDATE_GOOGLE_SUCCESS,
} from "./actionTypes";

const getListParams = (params, state) => ({
  page: params.page ?? state?.pagination?.page ?? 1,
  per_page: params.per_page ?? state?.pagination?.per_page ?? 10,
  search: params.search ?? state?.pagination?.search ?? "",
  scope: params.scope ?? "list",
  silent: params.silent ?? false,
});

const getResponseBody = (response) => response?.data ?? response;

const toList = (value) => (Array.isArray(value) ? value : []);

const buildSuccessPayload = (response, { search, scope }) => {
  const data = getResponseBody(response);
  const items = toList(data.data);

  const payload = {
    total: data.total ?? items.length,
    page: data.page ?? 1,
    per_page: data.per_page ?? 10,
    search,
    scope,
  };

  if (scope === "defective") {
    payload.defective = items;
  } else {
    payload.google = items;
  }

  return payload;
};

const reloadGoogleList = (dispatch, getState, overrides = {}) => {
  const { pagination } = getState().GOOGLE;
  dispatch(
    loadGoogle({
      page: pagination?.page ?? 1,
      per_page: pagination?.per_page ?? 10,
      search: pagination?.search ?? "",
      scope: "list",
      silent: true,
      ...overrides,
    })
  );
};

export const loadGoogle = (params = {}) => (dispatch, getState) => {
  const listState = getState().GOOGLE;
  const { page, per_page, search, scope, silent } = getListParams(
    params,
    listState
  );

  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;

  const req = Api.get("/googleall/get", {
    params: { page, per_page, search, scope },
  });

  const onSuccess = (data) => {
    dispatch({
      payload: buildSuccessPayload(data, { search, scope }),
      type: GET_GOOGLE_SUCCESS,
    });
  };

  if (silent) {
    req.then((response) => onSuccess(response)).catch(() => {});
    return;
  }

  toast.promise(req, {
    pending: "Getting Google IDs...",
    success: {
      render({ data: response }) {
        onSuccess(response);
        return "Your Data Is Ready";
      },
    },
    error: {
      render() {
        return "An error occurred while getting googles IDs";
      },
    },
  });
};

export const addGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/add", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadGoogleList(dispatch, getState, { page: 1 });
        return "Google ID added successfully";
      },
    },
    error: {
      render({ data }) {
        return data?.response.data?.message;
      },
    },
  });
};

export const updateGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/update", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render({ data }) {
        dispatch({
          payload: data.data.data,
          type: UPDATE_GOOGLE_SUCCESS,
        });
        reloadGoogleList(dispatch, getState);
        return "Google ID updated successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while updating your Google ID";
      },
    },
  });
};

export const deleteGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/delete", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadGoogleList(dispatch, getState);
        return "Google ID deleted successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while deleting your Google ID";
      },
    },
  });
};

export const soldGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/sold", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadGoogleList(dispatch, getState);
        return "Google ID updated successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while updating your Google ID";
      },
    },
  });
};

export const importGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  Api.defaults.headers.common["Content-Type"] = "multipart/form-data";
  let req = Api.post("/google/import", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadGoogleList(dispatch, getState, { page: 1, search: "" });
        return "Google IDs imported successfully";
      },
    },
    error: {
      render({ data }) {
        return data?.response.data?.message;
      },
    },
  });
};

export const exportGoogle = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/export", { number: data.formData.number });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render(response) {
        reloadGoogleList(dispatch, getState, { page: 1 });
        window.open(response.data.data, "_blank");
        return "Google IDs exported successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const exportGoogleQr = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/export/qr", {
    number: data.number,
    image: data.image,
    shownote: data.shownote,
    note: data.note,
  });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render(response) {
        reloadGoogleList(dispatch, getState, { page: 1 });
        window.open(response.data.data, "_blank");
        return "Google IDs exported successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const fixApple = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/fix", { accounts: data.formData.accounts });

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        reloadGoogleList(dispatch, getState, { page: 1 });
        return "Apple IDs fixed successfully";
      },
    },
    error: {
      render(err) {
        return err?.data.response.data.error;
      },
    },
  });
};

export const deleteAppleDefective = (data) => (dispatch, getState) => {
  Api.defaults.headers.common["Authorization"] =
    "Bearer " + getState().login.token;
  let req = Api.post("/google/delete/defective", data);

  toast.promise(req, {
    pending: "Loading...",
    success: {
      render() {
        dispatch(
          loadGoogle({
            scope: "defective",
            page: getState().GOOGLE.pagination?.page ?? 1,
            per_page: getState().GOOGLE.pagination?.per_page ?? 10,
            search: getState().GOOGLE.pagination?.search ?? "",
            silent: true,
          })
        );
        return "Apple ID deleted successfully";
      },
    },
    error: {
      render() {
        return "An error occurred while deleting your Apple ID";
      },
    },
  });
};
